import * as React from "react";
import { CircleCheck, CircleSlash, CircleX, CloudOff, MapPin, Pause, Play } from "lucide-react";
import { AngleGauge } from "@/components/charts/AngleGauge";
import { SeverityChip } from "@/components/ui/Badge";
import { IconList } from "@/components/ui/IconList";
import { IconTile } from "@/components/ui/IconTile";
import { cn } from "@/lib/cn";
import { usePrefersReducedMotion, useLoopingClock } from "@/lib/motion";
import {
  BUDGET_SECONDS,
  FINDINGS,
  SWEEP_SECONDS,
  HEIGHTS_ANGLE,
  HEIGHTS_ATTESTED,
  HEIGHTS_CHECKS,
  HEIGHTS_STEP_SPLIT,
  PPE_CHECKS,
  detectionVerdict,
  formatClock,
  phaseAt,
  type Phase,
  type ScriptedDetection,
} from "./assessment";
import { SecondScale } from "./SecondScale";

/**
 * The hero: one whole assessment, replayed.
 *
 * The most characteristic thing about this product is not a feature, it is a
 * *duration* - ninety seconds, on a phone, in the field, with the job waiting.
 * So the hero is the ninety seconds themselves: the real severity chips, the
 * real angle instrument, the real checklists off the module catalogue. A
 * screenshot would show what the app looks like; this shows what using it is
 * like, which is the thing an EHS director is actually buying.
 *
 * **It is replayed at four times speed and says so on screen.** Nobody watches a
 * landing page for a minute and a half, and an animation that quietly compressed
 * ninety seconds into twenty would be selling the product by misstating its one
 * headline number. The clock in the chrome still reads in real seconds.
 *
 * With reduced motion the clock never starts and the device renders the finished
 * assessment. That is the honest still frame - an empty viewfinder would be the
 * first frame, not the point.
 *
 * ── One skeleton, five screens ─────────────────────────────────────────────
 *
 * Every screen is chrome, an optional sub-head, a **frame**, and content. They
 * used to each lay themselves out, which is why one bled to the device edges
 * under a white status bar while the next sat inset - two screens of the same
 * app that did not look like the same app.
 *
 * ── Nothing here is ever empty ─────────────────────────────────────────────
 *
 * The first version of the camera screens was corner brackets on a black
 * rectangle and a caption. On a landing page an empty state is the one thing a
 * product visual must not be, and three blank thumbnails underneath it were the
 * same mistake twice.
 *
 * Two changes fix it and both make the thing *truer* rather than merely fuller.
 * The frame carries the detector's own output - a labelled box per row, anchored
 * where it sits - so what fills it is the product's answer rather than a stand-in
 * for a photograph nobody can show. And the rows exist from the first frame in a
 * pending state and resolve one at a time, which is both what a list being
 * answered looks like and the reason the layout no longer jumps or leaves a hole
 * under three of five rows.
 */

const SPEED = 2.5;

export function AssessmentReplay({ className }: { className?: string }) {
  const reduced = usePrefersReducedMotion();
  const [playing, setPlaying] = React.useState(true);

  const clock = useLoopingClock({
    duration: BUDGET_SECONDS,
    speed: SPEED,
    running: playing && !reduced,
    holdAtEnd: 1.4,
  });

  const elapsed = reduced ? BUDGET_SECONDS : clock;
  const phase = phaseAt(elapsed);

  return (
    <div className={cn("replay", className)}>
      <div className="replay__head">
        <p className="eyebrow eyebrow--sm eyebrow--panel-subtle">Ninety-second budget</p>
        {!reduced && (
          <button type="button" onClick={() => setPlaying((v) => !v)} className="replay__toggle">
            {playing ? <Pause /> : <Play />}
            {playing ? "Pause" : "Play"} · {SPEED}x speed
          </button>
        )}
      </div>

      <div className="replay__device">
        <div className="replay__screen">
          {/*
           * The chrome: what is happening, and how far into the ninety seconds
           * it is. Those two facts used to be four - a "No signal" chip that
           * never cleared, a clock, a caption under the screen naming the phase,
           * and a labelled ruler under that. The chip made a claim the replay
           * could not keep once a detector's rows appeared on it; the caption
           * delivered the most important word on the screen last and smallest;
           * and the ruler was the clock again, in a bar that looked seekable and
           * was not.
           */}
          <div className="replay__chrome">
            <p className="replay__phase">{phase.label}</p>
            <span className="replay__clock">
              {formatClock(elapsed)} / {formatClock(BUDGET_SECONDS)}
            </span>
          </div>

          <div className="replay__stage">
            {phase.id === "scan" && <ScanScreen elapsed={elapsed} phase={phase} />}
            {phase.id === "acknowledge" && <FindingsScreen elapsed={elapsed} />}
            {phase.id === "heights" && <HeightsScreen elapsed={elapsed} phase={phase} />}
            {phase.id === "ppe" && <PpeScreen elapsed={elapsed} phase={phase} />}
            {phase.id === "signoff" && <SignoffScreen />}
          </div>
        </div>
      </div>

      {/* The five phases, named and timed. The device says what is happening
          now; this says where that sits in the ninety seconds and what is still
          to come, which is the argument the page is actually making. */}
      <SecondScale elapsed={elapsed} tone="panel" animated className="replay__scale" />
    </div>
  );
}

/** How far through its own phase the replay is, 0 to 1. */
function progressIn(elapsed: number, phase: Phase): number {
  return Math.min(Math.max((elapsed - phase.from) / (phase.to - phase.from), 0), 1);
}

/* ──────────────────────────────── the frame ─────────────────────────────── */

/**
 * The camera surface, at one size wherever it appears.
 *
 * Dark in both themes, because it stands in for what the phone is pointed at and
 * that is never a white rectangle.
 *
 * ── It is the same box on every screen, deliberately ──────────────────────
 *
 * The sweep's frame used to take whatever height was left over and the two
 * detector frames were a 96px strip, so the one element common to three screens
 * was the element that looked least like itself between them. A fixed height
 * makes the three screens read as three moments in one app.
 *
 * ── And there is nothing drawn inside it ──────────────────────────────────
 *
 * It carried a labelled box per detection for a while. The boxes were accurate
 * about the shape of the output and dishonest about the thing that matters: they
 * sat at coordinates over an empty field, so they implied a photograph the
 * product cannot show and the page does not have. A frame that says "the camera
 * was here" claims nothing and needs no defending; the answers belong in the
 * rows, where they are words.
 */
function Frame({ sweep, children }: { sweep?: number; children?: React.ReactNode }) {
  return (
    <div className="frame">
      <span aria-hidden className="frame__grid" />
      {sweep !== undefined && (
        <span aria-hidden className="frame__sweep" style={{ top: `${6 + sweep * 88}%` }} />
      )}
      {children}
    </div>
  );
}

/* ─────────────────────────────── the screens ─────────────────────────────── */

function ScanScreen({ elapsed, phase }: { elapsed: number; phase: Phase }) {
  const progress = progressIn(elapsed, phase);
  const found = Math.min(Math.floor(progress * (FINDINGS.length + 1)), FINDINGS.length);

  return (
    <div className="screen">
      <div className="screen__head">
        <p className="screen__step">
          {SWEEP_SECONDS.min}&ndash;{SWEEP_SECONDS.max} seconds
        </p>
      </div>

      <Frame sweep={progress}>
        <p className="frame__note">
          {found > 0 ? `${found} finding${found === 1 ? "" : "s"} so far` : "Hold steady"}
        </p>
      </Frame>

      <div className="screen__foot">
        <p className="screen__title">Sweep the work area</p>
        <p className="screen__hint">Pan slowly. Nothing to aim at, nothing to frame.</p>
      </div>
    </div>
  );
}

function FindingsScreen({ elapsed }: { elapsed: number }) {
  const visible = FINDINGS.filter((finding) => elapsed >= finding.at).length;

  return (
    <div className="screen">
      <p className="screen__lead">
        <span className="screen__lead-strong">
          {visible} of {FINDINGS.length}
        </span>{" "}
        findings to acknowledge before work begins
      </p>

      {/* Every finding is in the DOM from the first frame and the ones not yet
          returned are held back as skeletons, so the list has its full height
          throughout. A list that grows pushes its own container about and leaves
          a hole under it, which is what this screen did. */}
      <ul className="findings">
        {FINDINGS.map((finding, index) => (
          <li key={finding.text} className={cn("finding", index >= visible && "finding--pending")}>
            {index < visible ? (
              <>
                <SeverityChip severity={finding.severity} />
                <p className="finding__text">{finding.text}</p>
              </>
            ) : (
              <>
                <span aria-hidden className="finding__chip-skeleton" />
                <span aria-hidden className="finding__text-skeleton" />
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Working at heights ──────────────────────────────────────────────────── */

/**
 * One module, two steps, because it records two different kinds of fact.
 *
 * **Step 1 is a measurement.** The angle comes off the accelerometer. It is not
 * an inference and carries no confidence - a phone resting on a rail either read
 * 74.8° or it did not - which is why the instrument is a gauge and no confidence
 * word appears anywhere near it.
 *
 * **Step 2 is an inference.** Feet level, tied off, rails undamaged, three feet
 * above the landing: all things a photograph answers and none of them anything
 * an inertial sensor ever will. They go to the same detector PPE's items do,
 * through the same endpoint, with the same three states per row.
 */
function HeightsScreen({ elapsed, phase }: { elapsed: number; phase: Phase }) {
  const progress = progressIn(elapsed, phase);
  const onReading = progress < HEIGHTS_STEP_SPLIT;

  return (
    <div className="screen">
      <div className="screen__head">
        <StepDots active={onReading ? 0 : 1} />
        <p className="screen__step">{onReading ? "Step 1 · the angle" : "Step 2 · the set-up"}</p>
      </div>

      {onReading ? (
        <AngleStep progress={progress / HEIGHTS_STEP_SPLIT} />
      ) : (
        <DetectorStep
          progress={(progress - HEIGHTS_STEP_SPLIT) / (1 - HEIGHTS_STEP_SPLIT)}
          reading="Reading the set-up"
          rows={HEIGHTS_CHECKS}
          attested={HEIGHTS_ATTESTED.map((item) => item.label)}
        />
      )}
    </div>
  );
}

/**
 * The reading, on the instrument the console already draws.
 *
 * `AngleGauge` rather than a circle with a number in it. The circle was a third
 * design of a thing that already had two - the phone's live dial and the
 * console's gauge - and the only one of the three drawing no tolerance band, so
 * it showed a reading without showing whether the reading passed.
 */
function AngleStep({ progress }: { progress: number }) {
  // Settles onto the reading over the first third of the step, the way an
  // inclinometer does when a phone is pressed flat against the rail.
  const settle = Math.min(progress * 3, 1);
  const angle = HEIGHTS_ANGLE.measured - (1 - settle) * 9;
  const captured = progress >= 0.8;

  return (
    <div className="angle">
      <AngleGauge
        angle={angle}
        target={HEIGHTS_ANGLE.target}
        tolerance={HEIGHTS_ANGLE.tolerance}
        className="angle__gauge"
      />

      <div className="angle__readout">
        <p className={cn("angle__value", settle >= 1 && "angle__value--ok")}>{angle.toFixed(1)}°</p>
        <p className="angle__target">
          target {HEIGHTS_ANGLE.target}° ± {HEIGHTS_ANGLE.tolerance}°
        </p>
      </div>

      <p className="screen__hint angle__hint">
        {captured
          ? "Reading captured. It stays with the photographs of the set-up."
          : `Phone flat against the rail. Hold still for ${HEIGHTS_ANGLE.holdSeconds} seconds and it takes itself.`}
      </p>
    </div>
  );
}

/* ── PPE ─────────────────────────────────────────────────────────────────── */

function PpeScreen({ elapsed, phase }: { elapsed: number; phase: Phase }) {
  return (
    <div className="screen">
      <div className="screen__head">
        <p className="screen__step">Before the climb</p>
      </div>

      <DetectorStep
        progress={progressIn(elapsed, phase)}
        reading="Reading the photograph"
        rows={PPE_CHECKS}
        columns
      />
    </div>
  );
}

/* ───────────────────────── the detector, both times ─────────────────────── */

/** Where the round trip ends and the answers start arriving. */
const READING = 0.3;

/**
 * Photograph, submit, read back - the half of a capture screen that is
 * "photograph these things, then ask".
 *
 * The middle beat is not decoration. A checklist app ticks a box the instant you
 * touch it; this one has to go and look, and the moment where it is looking is
 * the most legible difference between the two products a silent animation can
 * show. Naming what it is reading is the same choice the phone makes in
 * `WorkingStep`: "Working" tells a technician on one bar nothing about whether
 * to wait.
 *
 * The frame is tall while it is the only thing to look at and short once the
 * rows are the thing being read - the same demotion `AssessmentPanel` gives the
 * photographs when it puts them in a strip under the dial.
 */
function DetectorStep({
  progress,
  reading,
  rows,
  attested,
  columns,
}: {
  progress: number;
  reading: string;
  rows: ScriptedDetection[];
  /** Labels the detector is never asked about. Rendered last, as its own row. */
  attested?: string[];
  /** Two columns. Seven rows do not fit in one on this handset. */
  columns?: boolean;
}) {
  const analysing = progress < READING;

  // Rows resolve one at a time. All of them are on screen throughout, so the
  // list never grows into its own container and never leaves a hole under it.
  const total = rows.length + (attested?.length ?? 0);
  const resolved = analysing ? 0 : Math.ceil(((progress - READING) / (1 - READING)) * total);

  // The frame is the subject while there is nothing to read, and gone once
  // there is. Keeping it as a strip above the rows put an empty box on screen
  // for the longer half of the step, which is the thing this replay keeps
  // having to be talked out of.
  if (analysing) {
    return (
      <Frame sweep={(progress / READING) % 1}>
        <p className="frame__note frame__note--reading">{reading}</p>
      </Frame>
    );
  }

  return <RowList rows={rows} attested={attested} resolved={resolved} columns={columns} />;
}

/**
 * The checklist, from the first frame to the last.
 *
 * Every row is in the DOM throughout and the unanswered ones are held as
 * outlines, so the list has its full height from the moment the step opens.
 * A list that grows pushes its own container about and leaves a hole under it,
 * which is what both of these screens did.
 */
function RowList({
  rows,
  attested,
  resolved,
  columns,
}: {
  rows: ScriptedDetection[];
  attested?: string[];
  resolved: number;
  columns?: boolean;
}) {
  return (
    <ul className={cn("rows", columns && "rows--columns")}>
      {rows.map((row, index) => (
        <DetectionRow key={row.code} row={row} resolved={index < resolved} />
      ))}

      {/* The one row nothing optical will ever answer. It arrives after the
          detector's, exactly as it does on the phone, and is marked as a
          person's answer rather than dressed up as one of theirs. */}
      {attested?.map((label, index) => (
        <li
          key={label}
          className={cn("row row--attested", rows.length + index >= resolved && "row--pending")}
        >
          <span className="row__mark">
            <CircleCheck aria-hidden />
          </span>
          <div className="row__text">
            <p className="row__label">{label}</p>
            <p className="row__basis">Your call</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

const ROW_ICON = {
  present: CircleCheck,
  absent: CircleX,
  not_assessed: CircleSlash,
} as const;

/**
 * One row: a mark, a label, and what read it.
 *
 * "Found", "Not there", "Not in shot" - the three words `checkBasis` uses in
 * `AssessmentPanel.tsx`, because a technician and a supervisor reading the same
 * record must be told the same thing about it.
 *
 * ── Three words, and the confidence band is not one of them ───────────────
 *
 * The row carried "Found · High confidence" for a while. It is true, the console
 * shows it, and on a hero it was two words of jargon per row, seven rows at a
 * time, to qualify an answer nobody in the frame is going to act on. The band
 * belongs where somebody is deciding whether to trust a row; here it was
 * length. The verdict is the claim, and three words is the whole vocabulary.
 *
 * The second line still earns its place: a ticked box says somebody said yes,
 * and "Not in shot" says nothing looked - which is the difference between this
 * and a checklist, and the only place on the screen it is stated.
 */
function DetectionRow({ row, resolved }: { row: ScriptedDetection; resolved: boolean }) {
  const Icon = ROW_ICON[row.detected];

  return (
    <li className={cn("row", resolved ? `row--${row.detected}` : "row--pending")}>
      <span className="row__mark">{resolved ? <Icon aria-hidden /> : <RowSpinner />}</span>
      <div className="row__text">
        <p className="row__label">{row.label}</p>
        <p className="row__basis">{resolved ? detectionVerdict(row) : "Reading"}</p>
      </div>
    </li>
  );
}

function RowSpinner() {
  return <span aria-hidden className="row__spinner" />;
}

/** Which of the two steps is running. Two dots, so a count is beside the point. */
function StepDots({ active }: { active: number }) {
  return (
    <span aria-hidden className="step-dots">
      <span className={cn("step-dots__dot", active === 0 && "step-dots__dot--on")} />
      <span className={cn("step-dots__dot", active === 1 && "step-dots__dot--on")} />
    </span>
  );
}

function SignoffScreen() {
  return (
    <div className="signoff">
      <IconTile tone="ok" size="2xl">
        <CircleCheck />
      </IconTile>
      <p className="signoff__title">Assessment recorded</p>
      <IconList
        tone="subtle"
        layout="stack"
        align="center"
        className="signoff__list"
        items={[
          { icon: MapPin, text: "Geotagged and timestamped on the device" },
          { icon: CloudOff, text: "Queued - syncs when coverage returns" },
        ]}
      />
    </div>
  );
}
